import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Product {
  id: string;
  name: string;
  barcode: string;
  min_price: number;
  store_count: number;
  source?: string;
}

const sourceLabel = (source?: string) => {
  if (source === 'alphaamega') return { icon: '🛒', text: 'Alphaamega Direct', color: '#E67E22' };
  if (source === 'wolt') return { icon: '🚴', text: 'Wolt', color: '#009DE0' };
  return { icon: '📦', text: 'eKalathi Official', color: '#aaa' };
};

const ProductsPage = () => {
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [siteStats, setSiteStats] = useState({ products: 13031, stores: 45, wolt_stores: 1 });

  useEffect(() => {
    fetch('/api/products/stats/public')
      .then(r => r.json())
      .then(d => { if (d.products) setSiteStats(d); })
      .catch(() => {});
  }, []);
  const navigate = useNavigate();

  const doSearch = async (q: string) => {
    if (!q.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await api.get(`/products?search=${encodeURIComponent(q)}&size=20`);
      const data = res.data;
      const list = Array.isArray(data) ? data : (data.content || data.products || []);
      setProducts(list);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    doSearch(search);
  };

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', minHeight: '100vh', background: '#F0F4F8' }}>

      {/* Hero */}
      <div style={S.hero}>
        <h1 style={S.heroTitle}>Βρες τις καλύτερες τιμές<br/>στα σούπερ μάρκετ Κύπρου</h1>
        <p style={S.heroSub}>Δες πόσα θα εξοικονομείς · Έξυπνη λίστα αγορών · Σύγκριση ανάμεσα σε {siteStats.stores} αλυσίδες προϊόντων</p>

        <form onSubmit={handleSearch} style={S.searchForm}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Ψάξε προϊόν... π.χ. milk, water, tuna"
            style={S.searchInput}
            autoFocus
          />
          <button type="submit" style={S.searchBtn} disabled={loading}>
            {loading ? '...' : '🔍 Αναζήτηση'}
          </button>
        </form>

        <div style={S.quickSearch}>
          {['νερό', 'γάλα', 'τόνος', 'βούτυρο', 'ρύζι'].map(term => (
            <button key={term} style={S.quickBtn} onClick={() => { setSearch(term); doSearch(term); }}>
              {term}
            </button>
          ))}
        </div>
      </div>

      <div style={S.statsBar}>
        {[
          { num: siteStats.products.toLocaleString('el-GR') + '+', label: 'Προϊόντα' },
          { num: String(siteStats.stores), label: 'Καταστήματα' },
          { num: '10+', label: 'Καταστήματα Wolt' },
          { num: '100%', label: 'Δωρεάν' },
        ].map(s => (
          <div key={s.label} style={S.statItem}>
            <span style={S.statNum}>{s.num}</span>
            <span style={S.statLabel}>{s.label}</span>
          </div>
        ))}
      </div>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 16px' }}>
        {searched && (
          <>
            {loading && <p style={S.hint}>Αναζήτηση...</p>}
            {!loading && products.length === 0 && (
              <p style={S.hint}>Δεν βρέθηκαν αποτελέσματα για "{search}".</p>
            )}
            {!loading && products.length > 0 && (
              <>
                <p style={{ color: '#888', fontSize: 13, margin: '20px 0 12px' }}>
                  {products.length} αποτελέσματα για "{search}"
                </p>
                <div style={S.grid}>
                  {products.map(p => {
                    const src = sourceLabel(p.source);
                    return (
                      <div key={p.id} style={S.card}
                        onClick={() => navigate(`/products/${p.id}`)}>
                        <div style={S.cardBody}>
                          <p style={S.productName}>{p.name}</p>
                          <div style={S.row}>
                            <span style={S.price}>
                              από €{p.min_price ? Number(p.min_price).toFixed(2) : '—'}
                            </span>
                            <span style={S.chains}>{p.store_count} καταστήμ.</span>
                          </div>
                          <p style={{ ...S.source, color: src.color }}>
                            {src.icon} {src.text}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {!searched && (
          <div style={S.features}>
            {[
              { icon: '💰', title: 'Σύγκριση τιμών', desc: 'Δες αμέσως ποιο κατάστημα πουλάει το κάθε προϊόν φθηνότερα.' },
              { icon: '🛒', title: 'Έξυπνη λίστα', desc: 'Φτιάξε τη λίστα σου και η εφαρμογή σου λέει από πού να αγοράσεις το κάθε προϊόν.' },
              { icon: '💚', title: 'Μέτρησε την εξοικονόμηση', desc: 'Δες ακριβώς πόσα εξοικονομείς αγοράζοντας έξυπνα αντί από ένα μόνο κατάστημα.' },
            ].map(f => (
              <div key={f.title} style={S.featureCard}>
                <div style={S.featureIcon}>{f.icon}</div>
                <h3 style={S.featureTitle}>{f.title}</h3>
                <p style={S.featureDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        )}

        {!searched && (
          <div style={S.howItWorks}>
            <h2 style={S.howTitle}>Πώς λειτουργεί</h2>
            <div style={S.steps}>
              {[
                { num: '1', text: 'Ψάξε προϊόντα και πρόσθεσέ τα στη λίστα σου' },
                { num: '2', text: 'Επίλεξε σε ποια καταστήματα θέλεις να συγκρίνεις' },
                { num: '3', text: 'Δες το βέλτιστο πλάνο αγορών και πόσα κερδίζεις' },
              ].map(s => (
                <div key={s.num} style={S.step}>
                  <div style={S.stepNum}>{s.num}</div>
                  <p style={S.stepText}>{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  hero: { background: '#2952CC', padding: '48px 16px 40px', textAlign: 'center' },
  heroTitle: { color: 'white', fontSize: 28, fontWeight: 700, margin: '0 0 12px', lineHeight: 1.3 },
  heroSub: { color: '#B3C6FF', fontSize: 15, margin: '0 0 28px' },
  searchForm: { display: 'flex', maxWidth: 620, margin: '0 auto 16px', gap: 8 },
  searchInput: { flex: 1, padding: '14px 18px', borderRadius: 10, border: 'none', fontSize: 15, outline: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
  searchBtn: { padding: '14px 24px', background: '#EAF0FF', color: '#2952CC', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' as const },
  quickSearch: { display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' as const },
  quickBtn: { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '5px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer' },
  statsBar: { display: 'flex', justifyContent: 'center', gap: 0, background: '#1A3FA8', padding: '14px 16px' },
  statItem: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', padding: '0 24px', borderRight: '1px solid rgba(255,255,255,0.15)' },
  statNum: { color: 'white', fontWeight: 700, fontSize: 20 },
  statLabel: { color: '#B3C6FF', fontSize: 11, marginTop: 2 },
  hint: { color: '#888', textAlign: 'center', marginTop: 40 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 32 },
  card: { background: 'white', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', cursor: 'pointer' },
  cardBody: { padding: '12px 14px' },
  productName: { fontWeight: 600, fontSize: 14, color: '#2952CC', margin: '0 0 8px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  price: { color: '#27AE60', fontWeight: 700, fontSize: 15 },
  chains: { color: '#888', fontSize: 12 },
  source: { color: '#aaa', fontSize: 11, margin: '4px 0 0' },
  features: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16, margin: '32px 0' },
  featureCard: { background: 'white', borderRadius: 12, padding: '24px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', textAlign: 'center' as const },
  featureIcon: { fontSize: 36, marginBottom: 12 },
  featureTitle: { color: '#2952CC', fontSize: 16, fontWeight: 700, margin: '0 0 8px' },
  featureDesc: { color: '#5D6D7E', fontSize: 14, lineHeight: 1.6, margin: 0 },
  howItWorks: { background: 'white', borderRadius: 12, padding: '28px 24px', marginBottom: 32, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' },
  howTitle: { color: '#2952CC', fontSize: 18, fontWeight: 700, margin: '0 0 20px', textAlign: 'center' as const },
  steps: { display: 'flex', gap: 16, flexWrap: 'wrap' as const, justifyContent: 'center' },
  step: { display: 'flex', alignItems: 'flex-start', gap: 12, flex: 1, minWidth: 200 },
  stepNum: { width: 32, height: 32, borderRadius: '50%', background: '#2952CC', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, flexShrink: 0 },
  stepText: { color: '#444', fontSize: 14, lineHeight: 1.5, margin: '4px 0 0' },
};

export default ProductsPage;
